/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {RestResponse} from '../model';
import {SearchableProvider} from '../model/interface/searchable.provider';
import {ApiHeaders} from './headers';
import { TriggerType, ActionType, Automation, Action, ConditionType, Condition, Trigger } from '../modules/manage/model/automation';

@Injectable()
export class AutomationProvider implements SearchableProvider {

  constructor(
    private http: HttpClient
  ) {}

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number): Observable<RestResponse> {
    return this.getAutomations(page ? page : 1, query);
  }

  getAutomations(page: number, query?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/automation/list?page=${page}&sort=NAME&order=ASC` +
        (query ? '&query=' + encodeURIComponent(query) : ''),
      ApiHeaders.getOptions());
  }

  createAutomation(name: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/automation`, {
        NAME: name
      }, ApiHeaders.getOptions());
  }

  getAutomation(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/automation/${id}`, ApiHeaders.getOptions());
  }

  deleteAutomation(automation: Automation): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}`, ApiHeaders.getOptions());
  }

  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/automation/${id}/`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  saveAutomation(automation: Automation): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}`, automation, ApiHeaders.getOptions());
  }

  addTrigger(automation: Automation, type: TriggerType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}/trigger`, {
        VALUE: type
      }, ApiHeaders.getOptions());
  }

  saveTriggerField(id: string, trigger: Trigger): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/automation/trigger/${id}/field?type=${trigger.TYPE}`, trigger.JSON_FIELD, ApiHeaders.getOptions());
  }

  addAction(automation: Automation, type: ActionType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}/action`, {
        VALUE: type
      }, ApiHeaders.getOptions());
  }

  removeAction(automation: Automation, action: Action): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}/action/${action.ID}`, ApiHeaders.getOptions());
  }

  saveActionField(id: string, action: Action): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/automation/action/${id}/field?type=${action.TYPE}`, action.JSON_FIELD, ApiHeaders.getOptions());
  }

  // For Automation Condition
  addCondition(automation: Automation, type: ConditionType): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}/condition`, {
        VALUE: type
      }, ApiHeaders.getOptions());
  }

  removeCondition(automation: Automation, condition: Condition): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/automation/${automation.ID}/condition/${condition.ID}`, ApiHeaders.getOptions());
  }

  saveConditionField(id: string, condition: Condition): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/automation/condition/${id}/field?type=${condition.TYPE}`, condition.JSON_FIELD, ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'AUTOMATION';
  }
}
